/* eslint-disable @typescript-eslint/no-empty-function */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ResizeCallback } from 're-resizable';
import {
  Category,
  IProjectSpaceType,
  Subcategory,
} from 'spw-redux/modules/space_types/types';
import {
  SPWCancelButton,
  SPWDraggableResizableDialog,
  SPWInput,
  SPWSaveButton,
} from 'spw-shared/globalComponents';
import { colors } from 'spw-theme';
import { useAvgFormLogic } from 'spw-shared/hooks/use-avg-form-logic';
import { INDIVIDUAL_WORKSPACES_CATEGORY } from 'spw-constants';
import { useDispatch, useSelector } from 'react-redux';
import CurrencyInput from 'react-currency-input-field';
import {
  cleanWorksheetsUsingSpaceType,
  getWorksheetsUsingSpaceType,
  updateWorksheetRowsBySpaceType,
} from 'spw-redux/modules/program-summary/programming/actions';
import useUnitConversion from 'spw-components/unit-conversion/hooks';
import { MeasurementSystem } from 'spw-components/unit-conversion/types';
import { selectScenarioIdsUsingSpaceType } from 'spw-redux/modules/program-summary/programming/selectors';
import { RndBulkEditCardPopoverContent } from '../styles';
import { ScenariosTable } from './scenarios-table';

const DEFAULT_MIN_HEIGHT = 770;
const DIALOG_WITHOUT_TABLE_ESTIMATED_HEIGHT = 377;
const WIDTH = 630;

const useMinMax = (
  subject: 'USF' | 'Seats',
  category?: Category | Subcategory
) =>
  useMemo(() => {
    if (subject === 'Seats')
      return {
        min: 0,
        max: 5000,
      };

    return {
      min: category === INDIVIDUAL_WORKSPACES_CATEGORY ? 1 : 0,
      max: 50000,
    };
  }, [subject, category]);

interface IProps {
  projectSpaceType: Partial<IProjectSpaceType>;
  clientFacingNamesAreVisible?: boolean;
  subject: 'USF' | 'Seats';
  anchorEl: Element;
  onClose: () => void;
}

const BulkEditDialog = ({
  projectSpaceType,
  clientFacingNamesAreVisible,
  subject,
  anchorEl,
  onClose,
}: IProps) => {
  const {
    getUnit,
    convertToOriginalUnit,
    measurementSystem,
  } = useUnitConversion();

  const allowDecimals =
    measurementSystem === MeasurementSystem.metric && subject === 'USF';
  const minHeight =
    window.innerHeight < DEFAULT_MIN_HEIGHT
      ? window.innerHeight
      : DEFAULT_MIN_HEIGHT;

  const dispatch = useDispatch();

  const scenarioIdsUsingSpaceType = useSelector(
    selectScenarioIdsUsingSpaceType
  );

  const [dialogHeight, setDialogHeight] = useState(minHeight);
  const [scenariosChecked, setScenariosChecked] = useState<
    Record<number, boolean>
  >();
  const { min, max } = useMinMax(
    subject,
    projectSpaceType?.spaceType?.spaceTypeCategory?.category
  );

  const { value, floatValue, handleValueChanged } = useAvgFormLogic({ max });

  const isSomeScenarioChecked = useMemo(() => {
    if (scenarioIdsUsingSpaceType?.length)
      return scenarioIdsUsingSpaceType.some((id) => scenariosChecked?.[id]);

    return false;
  }, [scenarioIdsUsingSpaceType, scenariosChecked]);

  const save = () => {
    const scenarioIds = Object.keys(scenariosChecked)
      .map((id) => parseInt(id, 10))
      .filter((id) => scenariosChecked?.[id]);

    const isChild = !!projectSpaceType.spaceType.parent;

    const valueToUpdate =
      subject === 'USF'
        ? convertToOriginalUnit(floatValue, { unitType: 'area' })
        : floatValue;

    dispatch(
      updateWorksheetRowsBySpaceType({
        scenarioIds,
        spaceTypeId: projectSpaceType.id,
        isChild,
        [subject === 'Seats' ? 'seatCap' : 'usf']: valueToUpdate,
        callback: onClose,
      })
    );
  };

  useEffect(() => {
    if (anchorEl) {
      dispatch(getWorksheetsUsingSpaceType(projectSpaceType));
    }

    return () => {
      if (anchorEl) {
        dispatch(cleanWorksheetsUsingSpaceType());
      }
    };
  }, [anchorEl, dispatch, projectSpaceType]);

  const handleResize = ((_e, _d, element) => {
    setDialogHeight(element.getBoundingClientRect().height);
  }) as ResizeCallback;

  const { clientFacingName } = projectSpaceType;

  const descriptionText =
    subject === 'USF'
      ? `Changing the ${getUnit('area')} value updates the ${getUnit(
          'area'
        )} for all of these spaces in all of the scenarios selected below.`
      : `Changing the Seats value updates the seat capacity for all of these spaces in all of the scenarios selected below.`;

  const title = subject === 'USF' ? getUnit('area') : subject;

  const Input = useCallback(
    (props: Omit<React.ComponentPropsWithRef<'input'>, 'color' | 'size'>) => (
      <SPWInput
        {...props}
        variant="outlined"
        label={`NEW ${title.toUpperCase()}`}
      />
    ),
    [title]
  );

  return (
    <SPWDraggableResizableDialog
      anchorEl={anchorEl}
      onClose={onClose}
      onResize={handleResize}
      width={WIDTH}
      minHeight={minHeight}
      isVerticallyCenteredByDefault
      loading={!scenarioIdsUsingSpaceType?.length}
      ContentWrapper={RndBulkEditCardPopoverContent}
      title={
        <div className="title">
          <div>{subject === 'USF' ? getUnit('area') : subject}</div>
          <div className="name-type">
            {clientFacingNamesAreVisible ? 'Client Names' : 'Internal Names'}
          </div>
        </div>
      }
    >
      <div className="content">
        <div className="warning">CAUTION</div>
        <div className="description">
          <b>
            {clientFacingNamesAreVisible && clientFacingName
              ? clientFacingName
              : projectSpaceType.spaceType.name}
          </b>{' '}
          is being used in <b>{scenarioIdsUsingSpaceType?.length}</b> scenarios
          in this project. {descriptionText}
        </div>

        <CurrencyInput
          min={min}
          max={max}
          value={value}
          allowNegativeValue={false}
          onValueChange={handleValueChanged}
          allowDecimals={allowDecimals}
          customInput={Input}
        />

        <div className="table-wrapper">
          <ScenariosTable
            onChange={(newValue) => setScenariosChecked(newValue)}
            maxHeight={dialogHeight - DIALOG_WITHOUT_TABLE_ESTIMATED_HEIGHT}
            subject={subject}
            projectSpaceType={projectSpaceType}
          />
        </div>
      </div>
      <div className="actions">
        <SPWSaveButton onClick={save} disabled={!isSomeScenarioChecked} />
        <SPWCancelButton onClick={onClose} customColor={colors.black} />
      </div>
    </SPWDraggableResizableDialog>
  );
};

export default BulkEditDialog;
